
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { WorkspaceDetails } from "../models/WorkspaceDetails";
import { WorkspaceDetailsParams, Helper } from "../models/HelperClasses";
import { GraphUserModel } from "../models/GraphUserModel";
import PeopleSearch from "../components/PeopleSearch.vue";
import { mapState } from "vuex";
import {SizeAndPrice, VirtualMachine} from "../models/VirtualMachine";
import Doughnut from "@/components/charts/Doughnut.vue";
import VirtualMachineView from "@/components/WorkspaceDetails/VirtualMachineView.vue"
import AddVirtualMachine from "@/components/WorkspaceDetails/AddVirtualMachine.vue"
import VatWarning from '../components/shared/VatWarning.vue';
import EditVirtualMachine from "@/components/WorkspaceDetails/EditVirtualMachine.vue";
import { VreConfiguration } from "@/models/VreConfiguration";

@Component({
  components: {
    PeopleSearch,
    Doughnut,
    VirtualMachineView,
    AddVirtualMachine,
    VatWarning,
    EditVirtualMachine
  },
  computed: {
    ...mapState(["workspaceDetails", "vreConfiguration"])
  },
})
export default class WorkspaceDetail extends Vue {
  workspaceName: string = "";
  subscriptionId: string = "";
  showResizeInfo: boolean = false;
  selectedUser: GraphUserModel = new GraphUserModel();
  loading: boolean = false;
  reloading: boolean = false;
  uploading: boolean = false;
  vmPasswordLoading: boolean = false;
  displayedColumns: string[] = ["Name", "Select"];
  addingNewVM: boolean = false;

  refreshTimeout: any;
  vmLastUpdatedAt: any;
  nextRefreshAt: string = "00:00:00";

  public selectedSize!: SizeAndPrice;
  public workspaceDetails!: WorkspaceDetails; //Is assigned via mapstate
  public vreConfiguration!: VreConfiguration;
  private syncInterval: any;
  private timer: any;
  public elapsedTime: number = 0;
  public tabIndex: number = 0;

  get updatingVMset(){
    return this.workspaceDetails.virtualMachines.some(vm => {return vm.powerState === "Creating" || vm.powerState === "Deleting"})
        || this.workspaceDetails.vmSetUpdateInProgress;
  }

  get showAddVm(){
    return !this.updatingVMset && !this.addingNewVM;
  }

  get friendlyWorkspaceName() {
    return this.workspaceName.split("-")[1];
  }

  get excludeUsers() {
    var emails = [];
    for (var i = 0; i < this.workspaceDetails.owners.length; i++) {
      emails.push(this.workspaceDetails.owners[i].mail);
    }
    for (var i = 0; i < this.workspaceDetails.researchers.length; i++) {
      emails.push(this.workspaceDetails.researchers[i].mail);
    }
    return emails;
  }
  
  get budgetChartData() {
    if (
      !this.workspaceDetails ||
      !this.workspaceDetails.budget ||
      !this.workspaceDetails.budget.amount
    ) {
      return;
    }
    let amount = this.workspaceDetails.budget.amount;
    let currentSpend =
      Math.round(this.workspaceDetails.budget.currentSpend * 100) / 100;
    let currentLeft =
      Math.round((amount - this.workspaceDetails.budget.currentSpend) * 100) /
      100;
    let data = {
      datasets: [
        {
          backgroundColor: ["#99AABF", "#002C5F"],
          data: [currentSpend, currentLeft < 0 ? 0 : currentLeft],
        },
      ],
      // These labels appear in the legend and in the tooltips when hovering different arcs
      labels: [
        `Amount spent: ${Helper.formatMoney(currentSpend)} `,
        `Amount left: ${Helper.formatMoney(currentLeft)}`,
      ],
    };
    return data;
  }
  formatMoney(money: number){
    return money ? `€${money.toFixed(0)}` : "€0";
  }
  formatMoney2(money: number){
    return money ? `€${money.toFixed(2)}` : "€0";
  }

  get budgetEndDate() {
    if (
      !this.workspaceDetails ||
      !this.workspaceDetails.budget ||
      !this.workspaceDetails.budget.amount
    ) {
      return;
    }
    var endDate = new Date(this.workspaceDetails.budget.timePeriod.endDate);
    return Helper.formatDate(endDate);
  }

  get workspaceCostPerDay(): number {
    var baseCost = 0.6 * this.workspaceDetails.virtualMachines.length; //18/30 for disk etc.
    var totalCurrentCostPerDay = baseCost;
    this.workspaceDetails.virtualMachines.forEach(vm => {
      if (vm.currentSizeAndPrice) {
        totalCurrentCostPerDay += vm.currentSizeAndPrice && vm.powerState == "VM running" ? vm.currentSizeAndPrice.pricePerHour*24 : 0;
      }
    })
    return totalCurrentCostPerDay;
  }

  get daysOfBudgetRemaining(): number {
    var budgetleft = this.workspaceDetails.budget.amount * 0.95 - this.workspaceDetails.budget.currentSpend;
    return budgetleft / this.workspaceCostPerDay;
  }

  get formattedDaysOfBudgetRemaining(): string {
    return `${Math.floor(this.daysOfBudgetRemaining)}`;
  }

  get warningColorClass(): string{
    if(this.daysOfBudgetRemaining > 5){
      return "alert-success";
    }
    if(this.daysOfBudgetRemaining > 2){
      return "alert-warning";
    }
    return "alert-danger"
  }

  vmUpdated(vm:VirtualMachine) {
    this.log("VM updated", vm.name, vm.powerState, "last updated", this.vmLastUpdatedAt);
    this.vmLastUpdatedAt = Date.now();

    // reschedule refresh to 5 sec from now
    this.scheduleRefresh(this.vreConfiguration.workspaceRefreshInterval / 10);
  }

  async addVMStarted() {
    this.log("addVMStarted");
    this.addingNewVM = true;
    this.workspaceDetails.vmSetUpdateInProgress = true;
  }

  async addVMComplete() {
    this.log("addVMComplete");
    this.addingNewVM = false;
  }

  log(...args: any[]) {
    Helper.log("WS", this.workspaceDetails.timestamp, ":", ...args);
  }

  async updated() {
    this.addingNewVM = false;
  }

  async created() {
    this.log("creating...");
    this.$store.commit("addWorkspaceDetails", null);
    this.loading = true;
    await this.reload(true);
    this.loading = false;
    this.vmLastUpdatedAt = Date.now();

    this.scheduleRefresh(this.vreConfiguration.workspaceRefreshInterval);
    this.log("...created");
  }

  private doRefresh() {
    try {
      this.reload(false);
    }
    catch {
      console.error("Reload error.");
    }

    this.scheduleRefresh(this.vreConfiguration.workspaceRefreshInterval);
  }

  private scheduleRefresh(delay: number) {
    this.cancelRefresh();
    // disable refresh if interval <= 0 or > 10 min
    if (this.vreConfiguration.workspaceRefreshInterval <= 0 || this.vreConfiguration.workspaceRefreshInterval > 10*60*1000) {
      console.info("UI refresh disabled");
      return;
    }

    this.nextRefreshAt = Helper.ticksToTime(Date.now() + delay);
    this.refreshTimeout = setTimeout(() => this.doRefresh(), delay);
  }

  private cancelRefresh() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
  }

  async reload(init:boolean) {
    if (!init) {
      // dont recurse while in initial load
      if (this.loading)
        return;

      if (!this.workspaceDetails.virtualMachines)
        return;
    }

    // don't recurse in case it a slow day
    if (this.reloading) {
      this.log("skip reload - still reloading");
      return;
    }

    this.reloading = true;

    var workspaceDetailsParams = new WorkspaceDetailsParams(this.$route.params);
    this.workspaceName = this.$route.params.resourceGroupName;
    this.subscriptionId = this.$route.params.subscriptionId;

    // enable logging for "...Test..." workspaces
    if (this.workspaceName.indexOf("Test") > 0)
      Helper.enableLog = true;

    await this.$store.dispatch("getWorkspaceDetails", workspaceDetailsParams);

    this.reloading = false;
  }

  goToUpdateScreen(workspaceId: string){    
    this.$router.push({name: "UpdateWorkspace", params: {workspaceId: workspaceId}});
  }

  goToEditTab() {
    this.tabIndex = 3;
  }

  fileSelectionChanged() {
    //TODO will probably removed in favor of downloading directly or not at all
    // this.fileButtonDisabled = true;
    // for (let index = 0; index < this.workspaceDetails.workspaceItems.length; index++) {
    //   const element = this.workspaceDetails.workspaceItems[index];
    //   if (element.selected) {
    //     this.fileButtonDisabled = false;
    //     break;
    //   }
    // }
  }

  async removeResearcher(researcherValue: GraphUserModel) {
    var researcher = new GraphUserModel(researcherValue);
    var token = await this.$store.state.authModule.getapiTokenPopup();
    researcher.removeFromWorkspace(
      this.workspaceDetails.groupId,
      this.friendlyWorkspaceName,
      token
    );
    if(!this.workspaceDetails.isOwner){
      this.$router.push("/?forceRefresh=1");
    }
  }

  async addResearcher(researcherValue: GraphUserModel) {
    var researcher = new GraphUserModel(researcherValue);
    if (researcher) {
      let index = this.$store.state.workspaceDetails.researchers.indexOf(
        researcher,
        0
      );
      if (index == -1) {
        var token = await this.$store.state.authModule.getapiTokenPopup();
        await researcher.addToWorkspace(
          this.workspaceDetails.groupId,
          this.friendlyWorkspaceName, 
          token
        );
      }
    }
  }

  uploadFiles(): any {
    (this.$refs.fileInput as HTMLElement).click();
  }

  beforeDestroy() {
    this.log("destroying...");
    this.cancelRefresh();
    clearInterval(this.syncInterval);
    clearInterval(this.timer);
  }

  //Part of filestorage
  // async onFileChange(event: any) {
  //   let reader = new FileReader();
  //   if (event.target.files && event.target.files.length > 0) {
  //     this.uploading = true;
  //     let file = event.target.files.item(0);
  //     const formData: FormData = new FormData();
  //     formData.append(file.name, file, file.name);
  //     formData.append("resourceGroup", this.workspaceName);
  //     formData.append("storageAccount", this.storagename);
  //     formData.append("subscriptionId", this.subscriptionId);

  //     formData.append("container", "data");

  //     const url = process.env.VUE_APP_ROOT_API + `workspace/upload`;
  //     var result = await ajaxPostForm(url, formData);
  //     var workspaceDetailsParams = new WorkspaceDetailsParams(
  //       this.$route.params
  //     );
  //     await this.$store.dispatch("getWorkspaceDetails", workspaceDetailsParams);
  //     this.uploading = false;
  //   }
  // }
}
